import type { LucideIcon } from 'lucide-react';
import { createElement, forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/shared/lib/cn';

export type IconButtonVariant = 'default' | 'primary' | 'danger' | 'ghost' | 'outline';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  /** Accessible label for screen readers */
  label: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Optional notification badge count */
  badge?: number;
  /** Max value shown in badge before displaying "+" suffix */
  badgeMax?: number;
}

const variantStyles: Record<IconButtonVariant, string> = {
  default: 'text-secondary hover:bg-muted hover:text-body',
  primary: 'text-primary hover:bg-primary/10 hover:text-primary',
  danger: 'text-danger hover:bg-danger/10 hover:text-danger',
  ghost: 'text-secondary hover:bg-muted hover:text-body',
  outline:
    'border border-border bg-card text-body hover:bg-muted focus-visible:ring-primary',
};

const sizeStyles: Record<IconButtonSize, string> = {
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
  lg: 'h-10 w-10',
};

const iconSizeStyles: Record<IconButtonSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-5 w-5',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      label,
      variant = 'primary',
      size = 'md',
      badge,
      badgeMax = 9,
      className,
      disabled,
      ...props
    },
    ref,
  ) => {
    const showBadge = typeof badge === 'number' && badge > 0;
    const badgeLabel = showBadge
      ? badge > badgeMax
        ? `${String(badgeMax)}+`
        : String(badge)
      : null;

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        aria-label={
          showBadge ? `${label}, ${badgeLabel} unread` : label
        }
        className={cn(
          'relative inline-flex shrink-0 items-center justify-center rounded-lg transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-content-bg',
          'disabled:pointer-events-none disabled:opacity-50',
          variantStyles[variant],
          sizeStyles[size],
          className,
        )}
        {...props}
      >
        {createElement(icon, {
          className: iconSizeStyles[size],
          'aria-hidden': true,
        })}
        {showBadge && badgeLabel && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold leading-none text-white">
            {badgeLabel}
          </span>
        )}
      </button>
    );
  },
);

IconButton.displayName = 'IconButton';
